<template>
    <side-panel @close="close">
        <template #header>
            <h3 class="mt-0 clearfix">
                {{ title }}
            </h3>
        </template>
        <template #content>
            <preloader :display="loading" />

            <div v-if="!loading">
                <div class="prelative">
                    <preloader :display="processing" class="mt-5" />
                    <link-button
                        v-if="activeMode === 'read'"
                        class="fright"
                        :permissions="permissions"
                        permToCheck="can_edit_opportunity"
                        @clicked="activeEditMode"
                    >
                        {{ $l10n.__('Editer') }}
                    </link-button>
                    <div v-if="inputError" class="alert alert-danger mb-3">{{ $l10n.__('Certains champs sont invalides ou incomplets') }}</div>
                    <h3 class="mt-1 mb-3">{{ $l10n.__('Informations') }}</h3>
                    <el-form
                        v-show="!processing"
                        ref="opportunityForm"
                        labelPosition="top"
                        :model="opportunity"
                        :rules="rules"
                        :inlineMessage="true"
                        labelWidth="120px"
                    >
                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.opportunity_name')"
                            prop="name"
                            :error="errors.get('name')"
                        >
                            <TextInput
                                v-model="opportunity.name"
                                :mode="activeMode"
                                :placeholder="
                                    $t.__('forms.OpportunityForm.opportunity_placeholder')
                                "
                                data-bot="opportunity-name-input"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('common.labels.status')"
                            prop="status"
                            :error="errors.get('signed')"
                        >
                            <SelectInput
                                v-model="opportunity.signed"
                                :mode="activeMode"
                                :options="signedOptions"
                                idColumn="id"
                                data-bot="opportunity-status-input"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('common.labels.potential_amount')"
                            prop="potentialAmount"
                            :error="errors.get('potentialAmount')"
                        >
                            <AmountInput
                                v-model="opportunity.potentialAmount"
                                :mode="activeMode"
                                :placeholder="$t.__('common.labels.potential_amount')"
                                data-bot="opportunity-potential-amount-input"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.due_date')"
                            prop="dueDate"
                            :error="errors.get('dueDate')"
                        >
                            <DateInput
                                v-model="opportunity.dueDate"
                                :mode="activeMode"
                                :placeholder="$t.__('common.labels.date')"
                                :format="formatDate"
                                data-bot="opportunity-date-input"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('common.labels.pipeline')"
                            prop="funnelId"
                            :error="errors.get('funnelId')"
                        >
                            <SelectInput
                                v-model="opportunity.funnelId"
                                :mode="activeMode"
                                :options="funnelOptions"
                                labelColumn="name"
                                idColumn="id"
                                data-bot="opportunity-funnel-id-select"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('common.labels.step')"
                            prop="stepId"
                            :error="errors.get('stepId')"
                        >
                            <SelectInput
                                v-model="opportunity.stepId"
                                :mode="activeMode"
                                :options="stepOptions"
                                labelColumn="label"
                                idColumn="id"
                                data-bot="opportunity-step-select"
                                @update:modelValue="stepUpdated"
                            />
                        </el-form-item>
                        <el-form-item
                            :label="$t.__('common.labels.probability')"
                            prop="proba"
                            :error="errors.get('proba')"
                        >
                            <percent-input
                                v-model="opportunity.proba"
                                :mode="activeMode"
                                :placeholder="$t.__('common.labels.probability')"
                                data-bot="opportunity-probability-input"
                            />
                        </el-form-item>

                        <el-form-item
                            :label="$t.__('common.labels.note')"
                            prop="brief"
                            :error="errors.get('brief')"
                        >
                            <RichTextInput
                                v-model="opportunity.brief"
                                :mode="activeMode"
                                data-bot="opportunity-note-textarea"
                            />
                        </el-form-item>

                        <h3 class="mt-5 mb-3">{{ $l10n.__('Informations Sellsy') }}</h3>

                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.assigned_staff')"
                            prop="staffs"
                            :error="errors.get('staffs')"
                        >
                            <AutocompleteInput
                                v-model="opportunity.staffs"
                                :mode="activeMode"
                                :placeholder="$t.__('forms.OpportunityForm.assigned_staff')"
                                :options="staffOptions"
                                labelColumn="fullName"
                                idColumn="id"
                                data-bot="opportunity-staffs-input"
                                :relatedData="relatedData"
                            />
                        </el-form-item>

                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.id')"
                            prop="ident"
                            :error="errors.get('ident')"
                        >
                            <TextInput
                                v-model="opportunity.ident"
                                :mode="activeMode"
                                :placeholder="$t.__('forms.OpportunityForm.id')"
                                data-bot="opportunity-id-input"
                            />
                        </el-form-item>

                        <el-form-item
                            :label="$t.__('common.labels.source')"
                            prop="sourceId"
                            :error="errors.get('sourceId')"
                        >
                            <SelectInput
                                v-model="opportunity.sourceId"
                                :mode="activeMode"
                                :options="sourceOptions"
                                :placeholder="$t.__('common.labels.source')"
                                labelColumn="name"
                                idColumn="id"
                                data-bot="opportunity-source-id-select"
                            />
                        </el-form-item>

                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.linked_company')"
                            prop="linkedid"
                            :error="errors.get('linkedid')"
                        >
                            <AutocompleteRemoteInput
                                v-model="activelinkedid"
                                :relatedData="relatedData"
                                class="tags-150"
                                :mode="activeMode"
                                :placeholder="$t.__('forms.OpportunityForm.linked_company')"
                                :preset="opportunity.thirds"
                                :limit="1"
                                route="/rest/thirds/autocomplete"
                                data-bot="opportunity-corp-related-input"
                                @emptied="emptyContact"
                                @change="fetchContactForThird"
                            />
                        </el-form-item>

                        <el-form-item
                            :label="$t.__('forms.OpportunityForm.linked_contact')"
                            prop="contacts"
                            :error="errors.get('contacts')"
                        >
                            <AutocompleteInput
                                v-model="opportunity.contacts"
                                class="tags-150"
                                :mode="activeMode"
                                :placeholder="$t.__('forms.OpportunityForm.linked_contact')"
                                :options="contactOptions"
                                labelColumn="fullName"
                                idColumn="id"
                                :relatedData="relatedData"
                                data-bot="opportunity-contact-related-input"
                            >
                                <template #option>
                                    <el-option
                                        v-for="item in contactOptions"
                                        :key="item['id']"
                                        :label="item['fullName']"
                                        :value="parseInt(item['id'])"
                                    >
                                        <div class="flex-level">
                                            <span class="option-fullname mr-2">
                                                {{ item.fullName }}
                                            </span>
                                            <span class="grey flex-item-right regular ellipsis">
                                                {{ item.position }}
                                            </span>
                                        </div>
                                    </el-option>
                                </template>
                            </AutocompleteInput>
                        </el-form-item>

                        <!-- CUSTOM FIELDS -->
                        <!-- Ajouter mode edit -->
                        <section v-if="customFields.length > 0">
                            <h3 class="mt-5 mb-3">{{ $l10n.__('Champs personnalisés') }}</h3>

                            <!-- CF GROUP -->
                            <!-- check if custom field has value -->
                            <customfields
                                :cfs="customFields"
                                :cfv="customFieldValues"
                                :emitEditSuccess="emitEditSuccess"
                                :mode="activeMode"
                                :context="contextCFS"
                                :errors="errors"
                            />
                        </section>
                    </el-form>
                </div>
            </div>

            <CompassModal
                v-if="popupNewIdent"
                v-model:visible="popupNewIdent"
                size="large"
                :title="$t.__('forms.OpportunityForm.opportunity_number_already_exist')"
                class="opportunity-form__dialog"
                @closed="closePopupNewIdent"
            >
                <template #default>
                    <div class="dialog__default">
                        <span>
                            {{
                                $l10n.__(
                                    "Vous avez saisi un numéro d'opportunité existant. Souhaitez vous le modifier ?",
                                )
                            }}
                        </span>
                    </div>
                </template>
                <template #footer>
                    <div class="dialog__footer">
                        <CompassButton
                            :label="$t.__('common.actions.edit')"
                            type="primary"
                            @click="closePopupNewIdent"
                        />
                        <CompassButton
                            :label="$t.__('forms.OpportunityForm.save_opportunity')"
                            type="primary"
                            class="ml-2"
                            @click="saveWithoutNewIdent"
                        />
                        <CompassButton
                            :label="$t.__('forms.OpportunityForm.follow_numbering_and_continue')"
                            type="primary"
                            class="ml-2"
                            @click="saveWithNewIdent"
                        />
                    </div>
                </template>
            </CompassModal>
            <CompassModal
                v-model:visible="displayConfirmAutomation"
                size="small"
                :primaryAction="{
                    label: $t.__('forms.OpportunityForm.send_email'),
                    action: confirmAutomation,
                }"
                :secondaryActions="[
                    {
                        label: $t.__('forms.OpportunityForm.do_not_send_email'),
                        action: cancelAutomation,
                    },
                ]"
                :title="$t.__('forms.OpportunityForm.automatic_email_sending')"
                :closeOnClickOverlay="false"
                @closed="persistSave"
            >
                <template #default>
                    <span class="typographies-paragraph-medium-regular">
                        {{
                            $l10n.__(
                                "Une action d’envoi automatique d'email est activée sur cette étape. Confirmez-vous l'envoi de cet email ?",
                            )
                        }}
                    </span>
                </template>
            </CompassModal>
        </template>

        <template v-if="!loading" #footer>
            <div class="flex-level">
                <CompassIconButton
                    v-if="
                        permissions.can_delete_opportunity &&
                        mode !== 'add' &&
                        action !== 'duplicate'
                    "
                    icon="bin"
                    variant="destructive"
                    class="flex-item-left flex-level"
                    @click="goToUrl(buildUrl(deleteAction.value, parameters))"
                />

                <div class="flex-right">
                    <CompassButton
                        v-if="activeMode === 'edit'"
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        @click.prevent="close"
                    />
                    <CompassButton
                        v-if="activeMode === 'edit'"
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :disabled="processing || loading"
                        class="ml-1"
                        @click.prevent="submitForm('opportunityForm')"
                    />
                </div>
            </div>
        </template>
    </side-panel>
</template>

<script>
import { buildUrl, goToUrl } from './../../helpers';
import OpportunityRules from './rules/OpportunityRules';
import FormMixins from '../../mixins/FormMixins';
import Customfields from './Customfields.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import LinkButton from 'vcomponents/utilities/LinkButton.vue';
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import DateInput from 'vcomponents/inputs/DateInput.vue';
import RichTextInput from 'vcomponents/inputs/RichTextInput.vue';
import AutocompleteInput from 'vcomponents/inputs/AutocompleteInput.vue';
import AutocompleteRemoteInput from 'vcomponents/inputs/AutocompleteRemoteInput.vue';
import AmountInput from 'vcomponents/inputs/AmountInput.vue';
import PercentInput from 'vcomponents/inputs/PercentInput.vue';
import SidePanel from 'vcomponents/utilities/SidePanel.vue';
import { createNamespacedHelpers } from 'vuex';

const corpMapState = createNamespacedHelpers('corp').mapState;
const userMapState = createNamespacedHelpers('user').mapState;

export default {
    components: {
        Preloader,
        SidePanel,
        SelectInput,
        TextInput,
        DateInput,
        AutocompleteInput,
        AutocompleteRemoteInput,
        AmountInput,
        'percent-input': PercentInput,
        'link-button': LinkButton,
        RichTextInput,
        Customfields,
    },
    mixins: [FormMixins],
    props: {
        oppId: {
            type: [Number, Boolean, String],
            required: false,
            default: false,
        },
        linkedid: {
            type: [Number, Boolean],
            required: false,
            default: 0,
        },
        linkedtype: {
            type: [String],
            required: false,
            default: '',
        },
        mode: {
            type: String,
            required: false,
            default: 'read',
            validator: (value) => {
                return ['edit', 'read'].indexOf(value) !== -1;
            },
        },
        context: {
            type: String,
            required: false,
            default: 'default',
        },
        emitEditSuccess: {
            type: String,
            required: false,
            default: 'listing:refresh',
        },
        action: {
            type: String,
            required: false,
            default: '',
        },
    },

    data() {
        return {
            opportunity: {
                linkedid: this.linkedid,
                linkedtype: this.linkedtype,
                name: '',
                ident: '',
                signed: 'open',
                potentialAmount: 0,
                funnelId: 0,
                stepId: 0,
                sourceId: 0,
                createdDate: new Date().toString('yyyy-MM-dd HH:mm:ss'),
                dueDate: '',
                staffs: [],
                contacts: [],
            },
            rules: OpportunityRules,
            funnelOptions: [],
            sourceOptions: [],
            signedOptions: [],
            staffOptions: [],
            contactOptions: [],
            permissions: {},
            loading: false,
            popupNewIdent: false,
            newIdent: '',
            activelinkedid: this.linkedid,
            contextCFS: 'create',
            customFields: [],
            processing: false,
            inputError: false,
            originalStepId: 0,
            deleteAction: {},
            whenStepChangesUpdateProbability: true,
            activeMode: this.mode,
            displayConfirmAutomation: false,
        };
    },
    computed: {
        ...corpMapState({
            formatDate: 'timeformat',
        }),
        title() {
            return this.oppId ? this.opportunity.name : this.$l10n.__('Créer une opportunité');
        },
        stepOptions() {
            let funnel = {};
            if (this.opportunity.funnelId && this.funnelOptions.length > 0) {
                funnel = this.funnelOptions.find(
                    (funnel) => funnel.id == this.opportunity.funnelId,
                );
            }
            if (funnel && funnel.steps && funnel.steps.length > 0) {
                if (!funnel.steps.find((step) => step.id == this.opportunity.stepId)) {
                    this.setOppId(funnel.steps[0].id);
                    this.stepUpdated(this.opportunity.stepId);
                }
                return funnel.steps.map((step) => {
                    step.label =
                        step.name +
                        (step.hasAutomation
                            ? ' - ' + this.$l10n.__('email automatique activé')
                            : '');
                    return step;
                });
            } else {
                return [];
            }
        },
        relatedData() {
            if (this.oppId) {
                return {
                    type: 'opportunity',
                    id: this.oppId,
                };
            }
            return false;
        },
        parameters() {
            return [
                {
                    key: 'id',
                    value: this.oppId,
                },
                {
                    key: 'type',
                    value: this.linkedtype,
                },
                {
                    key: 'context',
                    value: this.context,
                    type: 'string',
                },
            ];
        },

        ...userMapState({
            userId: 'id',
        }),
    },
    created() {
        if (this.oppId) {
            this.contextCFS = 'edit';
            this.getOpportunity();
        } else {
            this.contextCFS = 'create';
            this.getMeta();
        }

        EventBus.on('opportunity:delete', this.onDelete);
    },
    unmounted() {
        EventBus.off('opportunity:delete', this.onDelete);
    },
    methods: {
        buildUrl,
        goToUrl,
        onDelete() {
            this.close();
            EventBus.emit('form:opportunity:delete', { stepId: this.opportunity.stepId });
        },
        activeEditMode() {
            this.activeMode = 'edit';
        },
        setOppId(id) {
            this.opportunity.stepId = id;
        },
        getOpportunity() {
            this.loading = true;
            this.$ajax.get(
                `/rest/prospection/opportunities/${this.oppId}`,
                {},
                (response) => {
                    this.funnelOptions = response.funnels;
                    this.sourceOptions = response.sources;
                    this.signedOptions = response.signedStatuses;
                    this.contactOptions = response.contacts;
                    this.formatData(response.data);
                    this.opportunity = Object.assign(this.opportunity, response.data);
                    this.opportunity.thirds = response.thirds.filter(
                        (third) => third.active !== false,
                    );
                    this.staffOptions = response.staffs.filter(
                        (staff) =>
                            staff.status === 'ok' ||
                            this.opportunity.staffs.includes(Number(staff.id)),
                    );
                    this.activelinkedid =
                        this.opportunity.thirds.length == 0 ? false : this.opportunity.linkedid;
                    this.customFields = response.customFields;
                    this.customFieldValues = response.data.customFieldValues;
                    this.permissions = response.permissions;
                    this.originalStepId = response.data.stepId;
                    this.whenStepChangesUpdateProbability = response.updateProba;
                    if (this.action === 'duplicate') {
                        this.opportunity.name = this.opportunity.name + this.$l10n.__(' - Copie');
                        this.opportunity.ident = response.nextident;
                        this.opportunity.originalId = this.oppId;
                        this.opportunity.createdDate = new Date().toString('yyyy-MM-dd HH:mm:ss');
                    }

                    const defaultSource = this.sourceOptions.find((source) => source.isDefault);
                    const firstSourceId = this.sourceOptions.length
                        ? this.sourceOptions[0].id
                        : null;
                    if (
                        !this.sourceOptions.find((source) => source.id == this.opportunity.sourceId)
                    ) {
                        this.opportunity.sourceId = defaultSource
                            ? defaultSource.id
                            : firstSourceId;
                    }

                    const { panelActions } = require('../overview/tabs/mapping/OpportunityMapping');
                    this.deleteAction = panelActions.find(
                        (action) => action.permission === 'can_delete',
                    );
                    this.loading = false;
                },
                () => {
                    this.close();
                    Push.error(this.$l10n.__("Problème lors de la récupération de l'opportunité"));
                },
            );
        },
        formatData(data) {
            if (data.dueDate == '' || data.dueDate == '0000-00-00 00:00:00') {
                data.dueDate = null;
            }
        },
        getMeta() {
            this.loading = true;
            this.$ajax.get(
                `/rest/prospection/opportunities/_meta`,
                { thirdId: this.linkedid },
                (response) => {
                    this.funnelOptions = response.funnels;
                    this.sourceOptions = response.sources;
                    this.signedOptions = response.signedStatuses;
                    this.staffOptions = response.staffs;
                    this.contactOptions = response.contacts;
                    this.customFields = response.customFields;
                    this.whenStepChangesUpdateProbability = response.updateProba;
                    this.customFieldValues = [];
                    this.setDefault(response);
                    this.activelinkedid = this.linkedid;
                    this.loading = false;
                },
                () => {
                    this.close();
                    Push.error(this.$l10n.__("Problème lors de la récupération de l'opportunité"));
                },
            );
        },
        save() {
            this.popupNewIdent = false;
            this.processing = true;

            /**
             * Check for automation before saving the form
             */
            let confirmForAutomation = false;
            if (this.originalStepId != this.opportunity.stepId) {
                const newFunnel = this.funnelOptions.find(
                    (funnel) => funnel.id == this.opportunity.funnelId,
                );
                const newStep = newFunnel.steps.find((step) => step.id == this.opportunity.stepId);
                confirmForAutomation = newStep.hasAutomation;
            }

            if (confirmForAutomation) {
                this.displayConfirmAutomation = true;
            } else {
                this.persistSave();
            }
        },
        confirmAutomation() {
            this.opportunity.processAutomation = true;
            this.displayConfirmAutomation = false;
        },
        cancelAutomation() {
            this.opportunity.processAutomation = false;
            this.displayConfirmAutomation = false;
        },
        persistSave() {
            this.opportunity.customFieldValues = this.customFieldValues;

            if (this.oppId && this.activeMode === 'edit' && this.action !== 'duplicate') {
                this.inputError = false;
                this.$ajax.put(
                    `/rest/prospection/opportunities/${this.oppId}`,
                    { _output: this.context },
                    this.opportunity,
                    (response) => {
                        Push.success(this.$l10n.__('Votre opportunité a été mise à jour'));
                        this.close();
                        this.processing = false;
                        EventBus.emit(this.emitEditSuccess, response);
                    },
                    (error) => {
                        this.errors.setErrors(error.response.data.error.message);
                        if (error.response.status == 400) {
                            this.inputError = true;
                        } else {
                            Push.error(
                                this.$l10n.__(
                                    "Une erreur est survenue lors de la modification de l'opportunité",
                                ),
                            );
                        }

                        this.processing = false;
                    },
                );
            } else {
                this.$ajax.post(
                    '/rest/prospection/opportunities',
                    {},
                    this.opportunity,
                    (response) => {
                        Push.success(this.$l10n.__('Votre opportunité a été créée'));
                        this.close();

                        this.processing = false;
                        this.popupNewIdent = false;
                        this.$router.push({
                            name: 'opportunity.overview.activity',
                            params: {
                                id: response.id,
                            },
                        });
                    },
                    (error) => {
                        if (error.response.data.error.code == 406) {
                            this.newIdent = error.response.data.error.message.ident;
                            this.popupNewIdent = true;
                            this.processing = false;
                        } else {
                            this.errors.setErrors(error.response.data.error.message);
                            Push.error(
                                this.$l10n.__(
                                    "Une erreur est survenue lors de l'enregistrement de l'opportunité",
                                ),
                            );
                            this.processing = false;
                        }
                    },
                );
            }
        },

        close() {
            this.$emit('close', this.opportunity);
        },
        setDefault(response) {
            const defaultFunnel = this.funnelOptions.find((funnel) => funnel.isDefault);
            this.opportunity.funnelId = defaultFunnel ? defaultFunnel.id : this.funnelOptions[0].id;

            const defaultSource = this.sourceOptions.find((source) => source.isDefault);
            const firstSourceId = this.sourceOptions.length ? this.sourceOptions[0].id : null;
            this.opportunity.sourceId = defaultSource ? defaultSource.id : firstSourceId;

            this.opportunity.signed = this.signedOptions[0].id;
            this.opportunity.ident = response.nextident;

            this.opportunity.staffs.push(this.userId);

            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + 30);
            this.opportunity.dueDate = currentDate.toString('yyyy-MM-dd 00:00:00');

            if (this.contactOptions.length > 0) {
                const mainContact = this.contactOptions.find((contact) => contact.mainContact);
                if (mainContact) {
                    this.opportunity.contacts.push(parseInt(mainContact.id));
                }
            }

            this.opportunity.thirds = response.thirds;
        },
        saveWithNewIdent() {
            this.opportunity.ident = this.newIdent;
            this.save();
        },
        saveWithoutNewIdent() {
            this.opportunity.forceident = true;
            this.save();
        },
        closePopupNewIdent() {
            this.popupNewIdent = false;
            this.loading = false;
        },
        emptyContact() {
            this.opportunity.contacts = [];
            this.contactOptions = [];
        },
        fetchContactForThird(thirdId) {
            this.emptyContact();
            this.opportunity.linkedid = thirdId;
            this.fecthAllThirdContacts(thirdId);
        },
        fecthAllThirdContacts(thirdId, currentPage = 0) {
            this.$ajax.get(
                `/rest/thirds/${thirdId}/contacts`,
                { page: currentPage + 1 },
                (response) => {
                    this.contactOptions = [...this.contactOptions, ...response.data];
                    if (response.page < response.nbrPages) {
                        this.fecthAllThirdContacts(thirdId, response.page);
                    }
                    const mainContactId = this.contactOptions.find(
                        (contact) => contact.mainContact,
                    )?.id;

                    if (this.opportunity.contacts.length === 0 && mainContactId) {
                        this.opportunity.contacts.push(mainContactId);
                    }
                },
                () => {
                    this.$pushToast({
                        type: 'error',
                        text: $t.__('forms.OpportunityForm.contacts_error'),
                        icon: true,
                    });
                },
            );
        },
        /** when step is updated, set opportunity probability with the current step probability */
        stepUpdated() {
            if (!this.whenStepChangesUpdateProbability) return;
            const selectedStep = this.stepOptions.find(
                (option) => option.id == this.opportunity.stepId,
            );
            if (selectedStep == undefined) {
                return;
            }
            this.opportunity.proba = selectedStep.probability;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.opportunity-form__dialog {
    z-index: 1001; // need it because sidepanel have z-index 1000
    .dialog {
        &__default {
            padding-bottom: var(--spacing-4);
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 var(--spacing-5) var(--spacing-4);
        }
    }
}

.el-select-dropdown__item.selected .option-fullname {
    font-weight: $font-weight-semibold;
}
</style>
